import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';

export interface LinkedFile {
  id: number;
  filename: string;
}

interface FileChangeImpactModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    selectedFileIds: number[],
    changedFields: Record<string, any>
  ) => void;
  linkedFiles: LinkedFile[];
  contextLabel: string; // e.g., parties or title change
  changedFields: Record<string, any>; // Only parties and/or title
  isLoading?: boolean;
}

export const FileChangeImpactModal: React.FC<FileChangeImpactModalProps> = ({
  open,
  onClose,
  onConfirm,
  linkedFiles,
  contextLabel,
  changedFields,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [selectedFileIds, setSelectedFileIds] = useState<Set<number>>(
    new Set()
  );
  const [partyOne, setPartyOne] = useState('');
  const [partyTwo, setPartyTwo] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const hasPartyChange =
    'party_one' in changedFields || 'party_two' in changedFields;
  const hasTitleChange = 'contract_title' in changedFields;

  useEffect(() => {
    if (open) {
      setSelectedFileIds(new Set(linkedFiles.map((f) => f.id)));
      setPartyOne(changedFields.party_one || '');
      setPartyTwo(changedFields.party_two || '');
      setTitle(changedFields.contract_title || '');
      setError(null);
    }
  }, [open, linkedFiles, changedFields]);

  const handleToggle = (fileId: number) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const handleSelectAll = () =>
    setSelectedFileIds(new Set(linkedFiles.map((f) => f.id)));
  const handleDeselectAll = () => setSelectedFileIds(new Set());

  const handleConfirm = () => {
    // Validate parties if party change is being applied
    if (hasPartyChange) {
      if (!partyOne.trim() || !partyTwo.trim()) {
        setError('Both parties are required when updating party information');
        return;
      }
    }

    // Build the payload with only changed fields
    const updatedFields: Record<string, any> = {};
    if (hasPartyChange) {
      updatedFields.party_one = partyOne;
      updatedFields.party_two = partyTwo;
    }
    if (hasTitleChange) {
      updatedFields.contract_title = title;
    }

    setError(null);
    onConfirm(Array.from(selectedFileIds), updatedFields);
  };

  const allSelected = selectedFileIds.size === linkedFiles.length;
  const noneSelected = selectedFileIds.size === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">{t('Update Related Files')}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t(
            'The following files are related to the file you just updated. You can apply the'
          )}{' '}
          {contextLabel} {t('changes to them as well.')}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Show input fields for parties and/or title */}
        {(hasPartyChange || hasTitleChange) && (
          <Box
            sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
              {t('Values to Apply:')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {hasPartyChange && (
                <>
                  <TextField
                    label={t('Party 1')}
                    value={partyOne}
                    onChange={(e) => setPartyOne(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    required
                  />
                  <TextField
                    label={t('Party 2')}
                    value={partyTwo}
                    onChange={(e) => setPartyTwo(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    required
                  />
                </>
              )}
              {hasTitleChange && (
                <TextField
                  label={t('Title')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                />
              )}
            </Box>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
          {t('Select Files to Update:')}
        </Typography>

        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={allSelected}
            variant="outlined"
          >
            {t('Select All')}
          </Button>
          <Button
            size="small"
            onClick={handleDeselectAll}
            disabled={noneSelected}
            variant="outlined"
          >
            {t('Deselect All')}
          </Button>
          <Typography
            variant="body2"
            sx={{ ml: 'auto', color: 'text.secondary' }}
          >
            {selectedFileIds.size} {t('of')} {linkedFiles.length}{' '}
            {t('selected')}
          </Typography>
        </Box>

        <List
          sx={{
            maxHeight: 350,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          {linkedFiles.map((file) => (
            <ListItem key={file.id} dense disablePadding>
              <ListItemButton
                dense
                onClick={() => handleToggle(file.id)}
                sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedFileIds.has(file.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={file.filename}
                  primaryTypographyProps={{
                    sx: {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isLoading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={noneSelected || isLoading}
        >
          {isLoading
            ? 'Updating...'
            : `Update ${selectedFileIds.size} File${
                selectedFileIds.size !== 1 ? 's' : ''
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
